import { ColumnDef } from "@tanstack/react-table";
import { BiDotsHorizontalRounded } from "react-icons/bi";
import { moneyFormat } from "../../../../../helpers/appHelpers";

// Helper functions
const formatDate = (params: any) => {
	if (!params.value) return "";
	const date = new Date(params.value);
	return date.toLocaleDateString();
};

const enumToStringConverter = (value: string) => {
	if (!value) return "";
	return value
		.split("_")
		.map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
		.join(" ");
};

// ==================== RATE TAX RULE COLUMNS ====================
export const rateTaxRuleColumns: ColumnDef<any>[] = [
	{
		accessorKey: "country",
		header: "Country",
		size: 200,
		minSize: 150,
		cell: ({ getValue }) => {
			const country = getValue() as any;
			return country ? country.name : "--";
		},
	},
	{
		accessorKey: "minTaxableAmount",
		header: "Min Taxable Amount",
		size: 220,
		minSize: 180,
		cell: ({ row }) => {
			const amount = row.original.minTaxableAmount;
			const currency = row.original.country?.currency;
			return `${currency?.code} ${moneyFormat(amount)}`;
		},
	},
	{
		accessorKey: "maxTaxableAmount",
		header: "Max Taxable Amount",
		size: 220,
		minSize: 180,
		cell: ({ row }) => {
			const amount = row.original.maxTaxableAmount;
			const currency = row.original.country?.currency;
			return `${currency?.code} ${moneyFormat(amount)}`;
		},
	},
	{
		accessorKey: "rateComponent",
		header: "Rate Component",
		size: 220,
		minSize: 180,
		cell: ({ getValue }) => `${(Number(getValue()) * 100).toFixed(2)}%`,
	},
	{
		accessorKey: "fixedComponent",
		header: "Fixed Component",
		size: 220,
		minSize: 180,
		cell: ({ row }) => {
			const currency = row.original.country?.currency;
			return `${currency?.code} ${moneyFormat(row.original.fixedComponent)}`;
		},
	},
	{
		accessorKey: "incomePeriod",
		header: "Income Period",
		size: 180,
		minSize: 130,
		cell: ({ getValue }) => enumToStringConverter(getValue() as string),
	},
	{
		accessorKey: "onDirectIncome",
		header: "On Direct Income",
		size: 200,
		minSize: 150,
		cell: ({ getValue }) => (getValue() ? "Yes" : "No"),
	},
	{
		accessorKey: "createdAt",
		header: "Date Created",
		size: 200,
		minSize: 150,
		cell: ({ getValue }) => formatDate({ value: getValue() }),
	},
	{
		accessorKey: "modifiedAt",
		header: "Date Modified",
		size: 200,
		minSize: 150,
		cell: ({ getValue }) => formatDate({ value: getValue() }),
	},
	{
		id: "actions",
		header: "Actions",
		size: 100,
		minSize: 80,
		cell: ({ row, table }) => {
			const meta = table.options.meta as any;
			return (
				<button
					onClick={(e) => {
						e.stopPropagation();
						if (meta?.onActionClick) {
							meta.onActionClick(row.original, e);
						}
					}}
					className="p-2 hover:bg-gray-100 rounded-full transition-colors"
				>
					<BiDotsHorizontalRounded className="text-xl" />
				</button>
			);
		},
	},
];

// ==================== BENEFITS TAX RULE COLUMNS ====================
export const benefitsTaxRuleColumns: ColumnDef<any>[] = [
	{
		accessorKey: "country",
		header: "Country",
		size: 200,
		minSize: 150,
		cell: ({ getValue }) => {
			const country = getValue() as any;
			return country ? country.name : "--";
		},
	},
	{
		accessorKey: "benefitTypeInstance",
		header: "Benefit Type",
		size: 200,
		minSize: 150,
		cell: ({ getValue }) => {
			const benefitType = getValue() as any;
			return benefitType ? benefitType.name : "--";
		},
	},
	{
		accessorKey: "minTaxableAmount",
		header: "Min Taxable Amount",
		size: 220,
		minSize: 180,
		cell: ({ row }) => {
			const amount = row.original.minTaxableAmount;
			const currency = row.original.country?.currency;
			return `${currency?.code} ${moneyFormat(amount)}`;
		},
	},
	{
		accessorKey: "maxTaxableAmount",
		header: "Max Taxable Amount",
		size: 220,
		minSize: 180,
		cell: ({ row }) => {
			const amount = row.original.maxTaxableAmount;
			const currency = row.original.country?.currency;
			return `${currency?.code} ${moneyFormat(amount)}`;
		},
	},
	{
		accessorKey: "rateComponent",
		header: "Rate Component",
		size: 220,
		minSize: 180,
		cell: ({ getValue }) => `${(Number(getValue()) * 100).toFixed(2)}%`,
	},
	{
		accessorKey: "fixedComponent",
		header: "Fixed Component",
		size: 220,
		minSize: 180,
		cell: ({ row }) => {
			const currency = row.original.country?.currency;
			return `${currency?.code} ${moneyFormat(row.original.fixedComponent)}`;
		},
	},
	{
		accessorKey: "isFinalTax",
		header: "Final Tax",
		size: 150,
		minSize: 100,
		cell: ({ getValue }) => (getValue() ? "Yes" : "No"),
	},
	{
		accessorKey: "createdAt",
		header: "Date Created",
		size: 200,
		minSize: 150,
		cell: ({ getValue }) => formatDate({ value: getValue() }),
	},
	{
		accessorKey: "modifiedAt",
		header: "Date Modified",
		size: 200,
		minSize: 150,
		cell: ({ getValue }) => formatDate({ value: getValue() }),
	},
	{
		id: "actions",
		header: "Actions",
		size: 100,
		minSize: 80,
		cell: ({ row, table }) => {
			const meta = table.options.meta as any;
			return (
				<button
					onClick={(e) => {
						e.stopPropagation();
						if (meta?.onActionClick) {
							meta.onActionClick(row.original, e);
						}
					}}
					className="p-2 hover:bg-gray-100 rounded-full transition-colors"
				>
					<BiDotsHorizontalRounded className="text-xl" />
				</button>
			);
		},
	},
];

// ==================== RELIEF TAX RULE COLUMNS ====================
export const reliefTaxRuleColumns: ColumnDef<any>[] = [
	{
		accessorKey: "country",
		header: "Country",
		size: 200,
		minSize: 150,
		cell: ({ getValue }) => {
			const country = getValue() as any;
			return country ? country.name : "--";
		},
	},
	{
		accessorKey: "taxReliefType",
		header: "Tax Relief Type",
		size: 220,
		minSize: 180,
		cell: ({ getValue }) => {
			const taxReliefType = getValue() as any;
			return taxReliefType ? taxReliefType.name : "--";
		},
	},
	{
		accessorKey: "rateComponent",
		header: "Rate Component",
		size: 220,
		minSize: 180,
		cell: ({ getValue }) => `${(Number(getValue()) * 100).toFixed(2)}%`,
	},
	{
		accessorKey: "fixedComponent",
		header: "Fixed Component",
		size: 220,
		minSize: 180,
		cell: ({ row }) => {
			const currency = row.original.country?.currency;
			return `${currency?.code} ${moneyFormat(row.original.fixedComponent)}`;
		},
	},
	{
		accessorKey: "incomePeriod",
		header: "Income Period",
		size: 180,
		minSize: 130,
		cell: ({ getValue }) => enumToStringConverter(getValue() as string),
	},
	{
		accessorKey: "amountDerivedFrom",
		header: "Amount Derived From",
		size: 220,
		minSize: 180,
		cell: ({ getValue }) => {
			const value = getValue() as string;
			return enumToStringConverter(value) || "--";
		},
	},
	{
		accessorKey: "createdAt",
		header: "Date Created",
		size: 200,
		minSize: 150,
		cell: ({ getValue }) => formatDate({ value: getValue() }),
	},
	{
		accessorKey: "modifiedAt",
		header: "Date Modified",
		size: 200,
		minSize: 150,
		cell: ({ getValue }) => formatDate({ value: getValue() }),
	},
	{
		id: "actions",
		header: "Actions",
		size: 100,
		minSize: 80,
		cell: ({ row, table }) => {
			const meta = table.options.meta as any;
			return (
				<button
					onClick={(e) => {
						e.stopPropagation();
						if (meta?.onActionClick) {
							meta.onActionClick(row.original, e);
						}
					}}
					className="p-2 hover:bg-gray-100 rounded-full transition-colors"
				>
					<BiDotsHorizontalRounded className="text-xl" />
				</button>
			);
		},
	},
];
